import React, { useState } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Skeleton } from 'primereact/skeleton';
import { CircleMinus } from 'lucide-react';

import { useConfirmDialogContext } from '../../../contexts/ConfirmDialog';
import Dialog from '../../shared/PFDialog/PFDialogContainer';
import PFButton from '../../shared/PFPrime/PFButton';
import PFDropdown from '../../shared/PFPrime/PFDropdown';
import PFMultiSelect from '../../shared/PFPrime/PFMultiSelect';

import DashboardFilterField from './DashboardFilterField';
import { CLEAR_ALL_FILTERS_CONFIRM, SOURCE } from './constants/constants';
import {
  findFilterDimensionByName,
  findGroupedFilterItem,
  getChildFilterOptions,
  getDimensionDisplayLabel,
  getParentFilterOptions,
  hasDashboardFilterValues,
} from './helpers/dashboard-filters.helper';
import './css/dashboard.styles.css';

const stopAccordionToggle = event => {
  event.preventDefault();
  event.stopPropagation();
};

const DashboardFilters = ({
  title = 'Apply filter',
  groupedOptions = [],
  appliedDimensions = [],
  onAppliedDimensionsChange,
  onSubmit,
  loading = false,
  submitLoading = false,
  disabled = false,
  type = undefined,
  filterValues = {},
  onFilterValuesChange,
  onApply,
  onClear,
  hasAppliedCubeFilters = false,
}) => {
  const { showConfirmationDialog } = useConfirmDialogContext();
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [canClearAllFilters, setCanClearAllFilters] = useState(false);
  const [isConfigureDialogVisible, setIsConfigureDialogVisible] =
    useState(false);

  const parentOptions = getParentFilterOptions(groupedOptions);
  const childOptions = getChildFilterOptions(
    groupedOptions,
    selectedParent,
    appliedDimensions.map(dimension => dimension.name)
  );

  const handleConfigureClick = event => {
    stopAccordionToggle(event);
    if (appliedDimensions?.length) {
      setCanClearAllFilters(true);
    }
    setIsConfigureDialogVisible(true);
  };

  const handleConfigureDialogHide = () => {
    setIsConfigureDialogVisible(false);
  };

  const handleClearAll = () => {
    showConfirmationDialog({
      header: CLEAR_ALL_FILTERS_CONFIRM.HEADER,
      message: CLEAR_ALL_FILTERS_CONFIRM.MESSAGE,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: CLEAR_ALL_FILTERS_CONFIRM.ACCEPT_LABEL,
      rejectLabel: CLEAR_ALL_FILTERS_CONFIRM.REJECT_LABEL,
      acceptClassName: 'p-button-primary p-button-sm',
      rejectClassName: 'p-button-outlined p-button-sm',
      baseZIndex: 2000,
      accept: async () => {
        const saved = await onSubmit?.([]);
        if (saved === false) {
          return;
        }
        setSelectedParent('');
        setSelectedChildren([]);
        setCanClearAllFilters(false);
        setIsConfigureDialogVisible(false);
      },
    });
  };

  const handleParentChange = (_name, value) => {
    setSelectedParent(value || '');
    setSelectedChildren([]);
  };

  const handleAdd = () => {
    const selectedNames = Array.isArray(selectedChildren)
      ? selectedChildren
      : [];
    if (!selectedNames.length) {
      return;
    }

    const existingNames = new Set(
      appliedDimensions.map(dimension => dimension.name)
    );
    const newDimensions = selectedNames
      .map(name => findGroupedFilterItem(groupedOptions, selectedParent, name))
      .filter(item => item?.value && !existingNames.has(item.value))
      .map(item => ({
        name: item.value,
        title: getDimensionDisplayLabel(item),
        shortTitle: item.shortTitle,
        type: item.type,
        group: item.group,
      }));

    if (newDimensions.length) {
      onAppliedDimensionsChange?.([...appliedDimensions, ...newDimensions]);
      setActiveIndex(0);
    }
    setSelectedChildren([]);
  };

  const handleSubmit = async () => {
    const saved = await onSubmit?.(appliedDimensions);
    if (saved === false) {
      return;
    }
    setIsConfigureDialogVisible(false);
  };

  const handleRemove = dimensionName => {
    onAppliedDimensionsChange?.(
      appliedDimensions.filter(dimension => dimension.name !== dimensionName)
    );
  };

  const renderFilterFields = (showRemove = false, fieldsPerRow = 4) => {
    if (!appliedDimensions?.length) {
      return (
        <div className="text-sm text-color-secondary">
          Select a group and filters, then click Add.
        </div>
      );
    }

    const columnClass =
      fieldsPerRow === 4 ? 'col-12 sm:col-6 md:col-3' : 'col-12';

    return (
      <div className="grid">
        {appliedDimensions.map(dimension => (
          <div key={dimension.name} className={`${columnClass} mb-2`}>
            <div className="flex align-items-start gap-2">
              <div className="flex-1">
                <DashboardFilterField
                  dimension={{
                    ...dimension,
                    title: getDimensionDisplayLabel(
                      findFilterDimensionByName(
                        groupedOptions,
                        dimension.name
                      ) || dimension
                    ),
                  }}
                  value={filterValues?.[dimension.name]}
                  onChange={(name, nextValue) =>
                    onFilterValuesChange?.({
                      ...filterValues,
                      [name]: nextValue,
                    })
                  }
                />
              </div>
              {showRemove && !disabled ? (
                <CircleMinus
                  className="text-primary cursor-pointer mt-2 w-1rem h-1rem"
                  onClick={() => handleRemove(dimension.name)}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const isApplyMode = type === SOURCE.HOME || type === SOURCE.DETAILS;
  const hasConfiguredFilters = Boolean(appliedDimensions?.length);

  if (isApplyMode && !hasConfiguredFilters) {
    return null;
  }

  const headerTemplate = (
    <div className="dashboard-filters-header flex justify-content-between align-items-center w-full">
      <span className="font-semibold white-space-nowrap">{title}</span>
      <div
        className="dashboard-filters-controls flex align-items-center"
        onClick={stopAccordionToggle}
        onMouseDown={stopAccordionToggle}
        onPointerDown={stopAccordionToggle}
      >
        {isApplyMode ? (
          <div className="flex align-items-center gap-2">
            <PFButton
              type="button"
              label="Clear"
              size="small"
              outlined
              disabled={
                disabled ||
                (!hasDashboardFilterValues(filterValues, appliedDimensions) &&
                  !hasAppliedCubeFilters)
              }
              onClick={() => onClear?.()}
            />
            <PFButton
              type="button"
              label="Apply"
              size="small"
              disabled={
                disabled ||
                !hasDashboardFilterValues(filterValues, appliedDimensions)
              }
              onClick={() => onApply?.()}
            />
          </div>
        ) : (
          <PFButton
            type="button"
            label="Configure"
            icon="pi pi-cog"
            size="small"
            outlined
            disabled={disabled || loading}
            onClick={handleConfigureClick}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Accordion
        activeIndex={activeIndex}
        onTabChange={event => setActiveIndex(event.index)}
        className="dashboard-filters w-full mb-1"
      >
        <AccordionTab header={headerTemplate}>
          {renderFilterFields(false, 4)}
        </AccordionTab>
      </Accordion>

      {!isApplyMode ? (
        <Dialog
          header="Configure filters"
          visible={isConfigureDialogVisible}
          onHide={handleConfigureDialogHide}
          draggable={false}
          blockScroll
          dismissableMask={false}
          className="dashboard-filters-dialog w-11 md:w-6 lg:w-4"
          contentClassName="dashboard-filters-dialog-content"
          pt={{
            footer: {
              className: 'justify-content-between',
            },
          }}
          footer={
            <div className="dashboard-filters-dialog-footer flex justify-content-between align-items-center gap-2 w-full">
              <PFButton
                type="button"
                label="Clear All"
                size="small"
                outlined
                disabled={
                  disabled ||
                  loading ||
                  submitLoading ||
                  (!appliedDimensions?.length && !canClearAllFilters)
                }
                onClick={handleClearAll}
              />
              <PFButton
                type="button"
                label="Submit"
                size="small"
                loading={submitLoading}
                disabled={
                  disabled ||
                  loading ||
                  submitLoading ||
                  !appliedDimensions?.length ||
                  selectedChildren?.length > 0
                }
                onClick={handleSubmit}
              />
            </div>
          }
        >
          <div className="dashboard-filters-dialog-body flex flex-column gap-3 w-full h-full">
            {loading ? (
              <>
                <Skeleton width="100%" height="2.5rem" />
                <Skeleton width="100%" height="2.5rem" />
              </>
            ) : (
              <>
                <span className="p-float-label mt-2">
                  <PFDropdown
                    name="filterParent"
                    inputId="dashboard-filter-parent"
                    value={selectedParent}
                    options={parentOptions}
                    optionLabel="label"
                    optionValue="value"
                    filter
                    showClear
                    className="w-full"
                    disabled={disabled}
                    onChange={handleParentChange}
                  />
                  <label htmlFor="dashboard-filter-parent">Select group</label>
                </span>
                <span className="p-float-label">
                  <PFMultiSelect
                    id="dashboard-filter-child"
                    value={selectedChildren}
                    options={childOptions}
                    optionLabel="label"
                    optionValue="value"
                    filter
                    showClear
                    showSelectAll={false}
                    display="chip"
                    maxSelectedLabels={2}
                    placeholder="Select filters"
                    className="w-full"
                    disabled={disabled || !selectedParent}
                    onChange={event => setSelectedChildren(event?.value || [])}
                  />
                  <label htmlFor="dashboard-filter-child">Select filters</label>
                </span>
              </>
            )}

            <div className="flex justify-content-end">
              <PFButton
                type="button"
                label="Add"
                size="small"
                outlined
                disabled={disabled || loading || !selectedChildren?.length}
                onClick={handleAdd}
              />
            </div>

            <div className="dashboard-filters-dialog-list">
              {renderFilterFields(true, 1)}
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
};

export default DashboardFilters;
